import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/States';
import { useEncounterList } from './hooks';
import { DISPOSITION_LABEL, IdentityBadge, STATUS_LABEL, minutesLabel, timeLabel } from './ui';

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/** Every encounter at this branch, searchable — including departed ones (identity, MLC and belongings continue after departure). */
export default function EncounterHistory({ onOpen }: { onOpen: (id: number) => void }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(today());
  const [mlc, setMlc] = useState(false);
  const [page, setPage] = useState(1);
  const list = useEncounterList({ q: q.trim() || undefined, status: status || undefined, from, to, mlc, page });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <Input className="w-[220px]" placeholder="Name, UHID or ER number" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <NativeSelect className="w-[170px]" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Any status</option>
          <option value="open">Still in the ED</option>
          <option value="departed">Departed</option>
        </NativeSelect>
        <Input type="date" className="w-[150px]" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        <Input type="date" className="w-[150px]" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        <Button variant={mlc ? 'primary' : 'ghost'} size="sm" onClick={() => { setMlc((m) => !m); setPage(1); }}>⚖ MLC only</Button>
      </div>
      {list.isLoading ? <LoadingState /> : list.isError ? <ErrorState message={(list.error as Error).message} /> :
        (list.data?.data.length ?? 0) === 0 ? <EmptyState label="No encounters match" /> : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[820px] text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-left text-[11px] uppercase tracking-wide text-ink-muted">
                  <th className="px-3 py-2">ER no.</th><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Arrived</th>
                  <th className="px-3 py-2">Level</th><th className="px-3 py-2">Door→doctor</th><th className="px-3 py-2">Status / disposition</th><th className="px-3 py-2">Attending</th>
                </tr>
              </thead>
              <tbody>
                {list.data!.data.map((r) => (
                  <tr key={r.id} onClick={() => onOpen(r.id)} className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/50">
                    <td className="px-3 py-2 font-mono text-[12px]">{r.er_number}{r.is_mlc && <Badge variant="warn" className="ml-1">MLC</Badge>}</td>
                    <td className="px-3 py-2"><div className="flex items-center gap-1.5">{r.patient_name}<IdentityBadge status={r.identity_status} /></div><div className="text-[11px] text-ink-muted">{r.uhid}{r.chief_complaint ? ` · ${r.chief_complaint}` : ''}</div></td>
                    <td className="px-3 py-2">{timeLabel(r.arrived_at)}</td>
                    <td className="px-3 py-2">{r.current_triage_level ?? '—'}</td>
                    <td className="px-3 py-2">{r.first_doctor_at ? minutesLabel((new Date(r.first_doctor_at).getTime() - new Date(r.arrived_at).getTime()) / 60000) : '—'}</td>
                    <td className="px-3 py-2">{r.disposition ? DISPOSITION_LABEL[r.disposition] : STATUS_LABEL[r.status]}</td>
                    <td className="px-3 py-2">{r.attending_doctor_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {list.data && (list.data.has_more || page > 1) && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Previous</Button>
          <Button size="sm" variant="ghost" disabled={!list.data.has_more} onClick={() => setPage((p) => p + 1)}>Next →</Button>
        </div>
      )}
    </div>
  );
}
